<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="operations"/>
        <pi-filters v-model="filters" :fields="filterFields" @clear="clearFilters"/>
        <div class="pi-ops__head">
          <pi-export-screen screen="Operações" :filters="exportFilters" :period="exportPeriodValue"/>
        </div>

        <section class="pi-section" aria-labelledby="pi-ops-table">
          <div class="pi-section__head">
            <h2 id="pi-ops-table" class="pi-section__title">Confiabilidade por operação</h2>
            <pi-definition-tooltip info-key="operations.reliability" :version="definitionVersion" align="left"/>
            <pi-state-legend/>
            <pi-export-menu title="Confiabilidade por operação" screen="Operações" :columns="columns" :rows="visibleRows" :envelope="request.envelope"
                            :filters="tableExportFilters" :period="exportPeriodValue" :disabled="!request.loaded || !!request.error || !rows.length"/>
          </div>
          <p class="pi-muted pi-ops__lead">
            Sucesso funcional ÷ tentativas distintas (retentativa da mesma chave conta 1 vez). Falha funcional = a regra recusou;
            falha técnica = exceção ou erro de infraestrutura. {{ attemptsLead }} {{ trendLead }}
          </p>
          <pi-data-state :loading="request.loading" :error="request.error" :envelope="request.envelope" :empty="isEmpty"
                         empty-message="Nenhuma operação medida no período." :show-footer="false" @retry="load(true)">
            <framed-table>
              <thead>
              <tr>
                <template v-for="column in headerColumns">
                  <pi-sort-th v-if="column.sortable" :key="column.key" :column="column" :state="sort" @sort="sortBy"/>
                  <pi-th v-else :key="column.key" :column="column"/>
                </template>
                <th scope="col"><span class="pi-sr">Detalhe</span></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in visibleRows" :key="row.key" :class="{ 'pi-row-selected': selectedKey === row.operationKey }">
                <td>
                  <strong>{{ row.label }}</strong>
                  <span v-if="row.operationKey" class="pi-table__sub"><code>{{ row.operationKey }}</code></span>
                  <span v-if="row.note" class="pi-table__sub">{{ row.note }}</span>
                </td>
                <td class="is-num">
                  <span v-if="!row.failuresMeasured" class="pi-ops__unmeasured" :title="row.failureTitle">—<span class="pi-sr"> {{ row.failureTitle }}</span></span>
                  <template v-else>
                    {{ row.attemptsText }}
                    <span v-if="row.dupText" class="pi-table__sub">{{ row.dupText }}</span>
                  </template>
                </td>
                <td class="is-num">
                  {{ row.successText }}
                  <span v-if="row.successLabel" class="pi-table__sub">{{ row.successLabel }}</span>
                </td>
                <td class="is-num">
                  <span v-if="!row.failuresMeasured" class="pi-ops__unmeasured" :title="row.failureTitle">—<span class="pi-sr"> {{ row.failureTitle }}</span></span>
                  <template v-else>
                    {{ row.businessText }}
                    <span v-if="row.businessShareText" class="pi-table__sub">{{ row.businessShareText }}</span>
                  </template>
                </td>
                <td class="is-num">
                  <span v-if="!row.failuresMeasured" class="pi-ops__unmeasured" :title="row.failureTitle">—<span class="pi-sr"> {{ row.failureTitle }}</span></span>
                  <template v-else>
                    {{ row.technicalText }}
                    <span v-if="row.technicalShareText" class="pi-table__sub">{{ row.technicalShareText }}</span>
                  </template>
                </td>
                <td class="is-num"><pi-metric-cell :cell="row.successRate"/></td>
                <td class="is-num">
                  <template v-if="row.hasDuration">{{ row.durationText }}</template>
                  <span v-else class="pi-table__sub">sem medição de duração</span>
                </td>
                <td><pi-sparkline :points="row.trend" :unit="row.trendUnit" :label="row.trendLabel" :width="96" :height="28"/></td>
                <td>
                  <button v-if="row.operationKey" type="button" class="pi-btn pi-btn--ghost"
                          :aria-pressed="selectedKey === row.operationKey ? 'true' : 'false'"
                          @click="select(row.operationKey)">{{ selectedKey === row.operationKey ? 'Fechar' : 'Detalhar' }}</button>
                </td>
              </tr>
              </tbody>
            </framed-table>
          </pi-data-state>
        </section>

        <section v-if="selectedKey && !blocked" class="pi-section" aria-labelledby="pi-ops-detail">
          <div class="pi-section__head">
            <h2 id="pi-ops-detail" class="pi-section__title">{{ selectedLabel }}</h2>
            <button type="button" class="pi-btn pi-btn--ghost" @click="select(selectedKey)">Fechar detalhe</button>
          </div>
          <div class="row">
            <div class="col-xs-12 col-lg-7">
              <pi-data-state :loading="series.loading" :error="series.error" :envelope="series.envelope"
                             :empty="series.loaded && !seriesPoints.length" empty-message="Sem série para esta operação no período."
                             :show-footer="false" @retry="loadDetail(true)">
                <template v-if="selectedRateReason">
                  <h3 class="pi-subtitle">Taxa de sucesso</h3>
                  <p class="pi-muted">n/d: {{ selectedRateReason }}</p>
                </template>
                <pi-line-chart v-else title="Taxa de sucesso" :series="charts.rate" unit="pct"
                               partial-label="Ponto vazado = queda marcada (ver lista abaixo)"
                               partial-flag="Queda marcada: taxa abaixo da média dos dias anteriores (ver lista abaixo)"/>
                <pi-line-chart :title="volumeTitle" :series="charts.volume"/>
                <template v-if="!selectedRateReason">
                  <h3 class="pi-subtitle">Quedas marcadas</h3>
                  <p class="pi-muted">{{ dropRuleText }}</p>
                  <ul v-if="drops.length" class="pi-ops__drops">
                    <li v-for="drop in drops" :key="drop.date">{{ drop.text }}</li>
                  </ul>
                  <p v-else class="pi-muted">Nenhuma queda pela regra no período.</p>
                </template>
              </pi-data-state>
            </div>
            <div class="col-xs-12 col-lg-5">
              <div class="pi-section__head">
                <h3 class="pi-subtitle">Categorias de falha</h3>
                <pi-export-menu title="Falhas por categoria" screen="Operações" :columns="failureColumns" :rows="failureRows" :envelope="failures.envelope"
                                :filters="failureExportFilters" :period="exportPeriodValue"
                                :disabled="!failures.loaded || !!failures.error || !failureRows.length"/>
              </div>
              <pi-data-state :loading="failures.loading" :error="failures.error" :envelope="failures.envelope"
                             :na-reason="selectedFailureReason"
                             :empty="failures.loaded && !failureRows.length && !totals.hasTotals" empty-message="Nenhuma falha registrada no período."
                             :show-footer="false" @retry="loadDetail(true)">
                <ul class="pi-stats">
                  <li class="pi-stat">
                    <span class="pi-stat__label">Falha funcional <pi-definition-tooltip info-key="operations.businessFail" label="Falha funcional"/></span>
                    <span class="pi-stat__value">{{ totals.businessText }}</span>
                    <span v-if="totals.businessShareText" class="pi-stat__sub">{{ totals.businessShareText }}</span>
                  </li>
                  <li class="pi-stat">
                    <span class="pi-stat__label">Falha técnica <pi-definition-tooltip info-key="operations.technicalFail" label="Falha técnica"/></span>
                    <span class="pi-stat__value">{{ totals.technicalText }}</span>
                    <span v-if="totals.technicalShareText" class="pi-stat__sub">{{ totals.technicalShareText }}</span>
                  </li>
                </ul>
                <div v-if="failureRows.length" class="pi-table-wrap">
                  <table class="pi-table">
                    <caption class="pi-sr">Falhas por categoria</caption>
                    <thead>
                    <tr>
                      <pi-th v-for="column in failureHeaderColumns" :key="column.key" :column="column"/>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in failureRows" :key="row.key">
                      <td>
                        <span v-if="row.categoryKnown" :title="row.category">{{ row.categoryText }}</span>
                        <code v-else>{{ row.category }}</code>
                      </td>
                      <td v-if="failureTypeShown">
                        <span v-if="row.hasType" class="pi-badge" :class="'pi-badge--' + row.result.tone" :title="row.result.description">{{ row.result.label }}</span>
                      </td>
                      <td class="is-num">{{ row.countText }}</td>
                      <td class="is-num">{{ row.shareText }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="pi-muted">A fonte não informou categorias para estas falhas.</p>
              </pi-data-state>
            </div>
          </div>
        </section>

        <pi-page-footer :envelopes="[request.envelope, series.envelope, failures.envelope]" :labels="['Operações', 'Série', 'Falhas']"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import framedTable from "../../components/default/framedTable.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piFilters from "../../components/pi/piFilters.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piLineChart from "../../components/pi/piLineChart.vue";
import piSparkline from "../../components/pi/piSparkline.vue";
import piMetricCell from "../../components/pi/piMetricCell.vue";
import piSortTh from "../../components/pi/piSortTh.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piStateLegend from "../../components/pi/piStateLegend.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import piTh from "../../components/pi/piTh.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { piGet } from "../../components/pi/piApi.js";
import { columnByKey, screenColumns } from "../../components/pi/piColumns.js";
import { exportPeriod, exportProvide, filterLines } from "../../components/pi/piExport.js";
import { PI_FILTER_DEFAULTS, PI_FILTER_KEY, buildApiQuery, compareEnabled, nextSort, sortRows, toQueryString } from "../../components/pi/piQuery.js";
import { blankRequest, cancelRequest, isBlocking, listOf, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import {
  ATTEMPTS_LEAD, DEFAULT_OPERATION_SORT, DROP_RULE, TREND_LEAD, failureColumns, failureRows, failureSummary, markDrops, operationColumns, operationRows,
  operationSeries, volumeTitle,
} from "../../components/pi/piOperationsModel.js";
import { PI_OPERATION_FAILURES, PI_OPERATION_SERIES, PI_OPERATIONS } from "../../../../urlConfig";

const FILTER_FIELDS = ["period", "compare", "module", "platform"];

export default {
  name: "pi-operations",
  components: {
    boxDefault, framedTable, piTabs, piFilters, piDataState, piLineChart, piSparkline, piMetricCell, piSortTh, piDefinitionTooltip, piStateLegend,
    piPageFooter, piExportMenu, piExportScreen, piTh,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    return {
      filterFields: FILTER_FIELDS,
      request: blankRequest(),
      series: blankRequest(),
      failures: blankRequest(),
      selectedKey: "",
      sort: Object.assign({}, DEFAULT_OPERATION_SORT),
      attemptsLead: ATTEMPTS_LEAD,
      trendLead: TREND_LEAD,
      dropRuleText: "Regra visual: taxa do ponto pelo menos " + DROP_RULE.thresholdPp + " p.p. abaixo da média dos até " +
        DROP_RULE.window + " pontos anteriores; só entram pontos com " + DROP_RULE.minAttempts +
        " tentativas ou mais. O alerta oficial continua sendo o insight R6.",
    };
  },
  computed: {
    compare() {
      return compareEnabled(this.filters);
    },
    columns() {
      return operationColumns(this.compare);
    },
    headerColumns() {
      return screenColumns(this.columns);
    },
    exportPeriodValue() {
      return exportPeriod(this.filters);
    },
    exportFilters() {
      return filterLines({ filters: this.filters, fields: FILTER_FIELDS });
    },
    sortLine() {
      const column = columnByKey(this.columns, this.sort.sort);
      return column ? "Ordem: " + column.label + (this.sort.desc ? ", decrescente" : ", crescente") : "";
    },
    tableExportFilters() {
      return this.exportFilters.concat([this.sortLine]);
    },
    failureExportFilters() {
      return this.exportFilters.concat([this.selectedRow ? "Operação: " + this.selectedRow.label : ""]);
    },
    failureColumns() {
      return failureColumns(this.failureTypeShown);
    },
    failureHeaderColumns() {
      return screenColumns(this.failureColumns);
    },
    params() {
      return buildApiQuery(this.filters, { fields: FILTER_FIELDS });
    },
    apiKey() {
      return toQueryString(this.params);
    },
    detailKey() {
      return this.selectedKey ? this.selectedKey + "?" + this.apiKey : "";
    },
    blocked() {
      return isBlocking(this.request.error);
    },
    definitionVersion() {
      return this.request.envelope ? this.request.envelope.definitionVersion : null;
    },
    rows() {
      return operationRows(payloadOf(this.request.envelope).rows, this.compare);
    },
    visibleRows() {
      return sortRows(this.rows, this.sort.sort, this.sort.desc);
    },
    isEmpty() {
      return this.request.loaded && !this.rows.length;
    },
    selectedRow() {
      return this.rows.filter((item) => item.operationKey === this.selectedKey)[0] || null;
    },
    selectedLabel() {
      return "Detalhe: " + (this.selectedRow ? this.selectedRow.label : this.selectedKey);
    },
    selectedFailuresMeasured() {
      return !this.selectedRow || this.selectedRow.failuresMeasured;
    },
    selectedSuccessLabel() {
      return this.selectedRow ? this.selectedRow.successLabel : "";
    },
    volumeTitle() {
      return volumeTitle(this.selectedFailuresMeasured, this.selectedSuccessLabel);
    },
    selectedFailureReason() {
      return this.selectedFailuresMeasured ? "" : this.selectedRow.failureTitle;
    },
    selectedRateReason() {
      if (!this.selectedRow || !this.selectedRow.noSuccessRate) return "";
      return this.selectedRow.successRate.reason || this.selectedRow.successRate.title;
    },
    seriesPoints() {
      return listOf(payloadOf(this.series.envelope).points);
    },
    charts() {
      return operationSeries(this.seriesPoints, this.selectedFailuresMeasured, this.selectedSuccessLabel);
    },
    drops() {
      return markDrops(this.seriesPoints);
    },
    failureRows() {
      return failureRows(payloadOf(this.failures.envelope).rows);
    },
    failureTypeShown() {
      return this.failureRows.some((row) => row.hasType);
    },
    totals() {
      return failureSummary(payloadOf(this.failures.envelope).rows);
    },
  },
  watch: {
    apiKey() {
      this.load(false);
    },
    detailKey() {
      this.loadDetail(false);
    },
  },
  created() {
    this.load(false);
  },
  methods: {
    load(force) {
      const key = this.apiKey;
      if (!shouldLoad(this.request, key, force)) return;
      runRequest(this.request, () => piGet(PI_OPERATIONS, this.params), "carregar as operações", key);
    },
    loadDetail(force) {
      const key = this.detailKey;
      if (!key) {
        cancelRequest(this.series);
        cancelRequest(this.failures);
        return;
      }
      const operationKey = this.selectedKey;
      const params = this.params;
      if (shouldLoad(this.series, key, force)) {
        runRequest(this.series, () => piGet(PI_OPERATION_SERIES(operationKey), params), "carregar a série da operação", key);
      }
      if (shouldLoad(this.failures, key, force)) {
        runRequest(this.failures, () => piGet(PI_OPERATION_FAILURES(operationKey), params), "carregar as falhas da operação", key);
      }
    },
    select(operationKey) {
      this.selectedKey = this.selectedKey === operationKey ? "" : operationKey;
    },
    sortBy(column) {
      this.sort = nextSort(this.sort, column.key, column.startDescending);
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-ops__head {
  display: flex;
  justify-content: flex-end;
  margin: 0 0 8px;
}

.pi-ops__lead {
  margin-bottom: 12px;
}

.pi-ops__unmeasured {
  color: #6b7280;
  cursor: help;
}

.pi-ops__drops {
  margin: 0 0 12px;
  padding-left: 18px;
  font-size: 12px;
  color: #3a424e;
}

.pi-page code {
  padding: 0;
  background: none;
  color: #1f2933;
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piOperations.vue